import React, { useEffect } from 'react';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';

import CheckCircleOutlineOutlined from '@mui/icons-material/CheckCircleOutlineOutlined';
import ErrorOutlineOutlined from '@mui/icons-material/ErrorOutlineOutlined';
import HealthAndSafetyOutlined from '@mui/icons-material/HealthAndSafetyOutlined';
import InfoOutlined from '@mui/icons-material/InfoOutlined';

import { useTranslation } from 'react-i18next';
import { FaRegHospital } from 'react-icons/fa';
import { BsJournalMedical } from 'react-icons/bs';

import { Loader, Tabs } from '@omnigenbiodata/ui';

import PortalLayout from '../../../../layout/Portal';
import { ROUTES } from '../../../../core/constants/routes.constant';
import { FEATURES, flagActive } from '../../../../core/constants/features.constants';

import { useAppDispatch, useAppSelector } from '../../../../store';
import { healthDataOptInSelector } from '../../../../store/participant/selectors';

import { getEhrStatusThunk, getVisitsThunk, getConditionsThunk } from '../../../../store/health';
import {
  ehrStatusSelector,
  isBusySelector,
  visitsSelector,
  conditionsSelector,
  healthErrorSelector,
} from '../../../../store/health/selectors';

import VisitsList from './components/VisitsList';
import EhrTab from './components/EhrTab';
import Conditions from './components/ConditionsList';
import { chartColours } from '../../../../core/constants/colours.constants';

function HealthScene() {
  const { t } = useTranslation('portal');
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const healthDataOptIn = useAppSelector(healthDataOptInSelector);
  const isBusy = useAppSelector(isBusySelector);
  const visits = useAppSelector(visitsSelector);
  const conditions = useAppSelector(conditionsSelector);
  const hasError = useAppSelector(healthErrorSelector);
  const ehrStatus = useAppSelector(ehrStatusSelector);

  useEffect(() => {
    if (healthDataOptIn) {
      dispatch(getEhrStatusThunk());
    } else {
      navigate(ROUTES.portal);
    }
  }, [dispatch, navigate, healthDataOptIn]);

  useEffect(() => {
    if (ehrStatus.dataHasArrived) {
      if (flagActive(FEATURES.EHR_VISITS)) {
        dispatch(getVisitsThunk());
      }

      if (flagActive(FEATURES.EHR_CONDITIONS)) {
        dispatch(getConditionsThunk());
      }
    }
  }, [ehrStatus, dispatch]);

  return (
    <PortalLayout>
      <Paper
        elevation={4}
        sx={{
          border: 1,
          borderColor: 'divider',
          borderRadius: 3,
          overflow: 'hidden',
          bgcolor: 'background.paper',
        }}
      >
        <Box
          sx={{
            px: { xs: 2.5, md: 3.5 },
            pt: { xs: 2.5, md: 3.5 },
            pb: 2.5,
            borderBottom: 1,
            borderColor: 'divider',
            bgcolor: 'background.paper',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.75 }}>
            <Box
              sx={{
                width: 54,
                height: 54,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: `${chartColours[1]}26`,
                color: chartColours[1],
                flexShrink: 0,
                border: 1,
                borderColor: `${chartColours[1]}55`,
              }}
            >
              <HealthAndSafetyOutlined sx={{ fontSize: 34 }} />
            </Box>

            <Box sx={{ minWidth: 0 }}>
              <Typography
                variant="h4"
                component="h1"
                sx={{
                  fontWeight: 900,
                  lineHeight: 1.15,
                }}
              >
                {t('health.title')}
              </Typography>

              {ehrStatus?.lastProcessedTime && (
                <Typography
                  variant="body2"
                  component="p"
                  color="text.secondary"
                  sx={{
                    mt: 0.75,
                    fontWeight: 700,
                  }}
                >
                  {t('health.subtitle')} {moment(ehrStatus.exportedDate).utcOffset('+0200').format('DD/MM/YYYY')}
                </Typography>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ p: { xs: 2.5, md: 3.5 } }}>
          {!isBusy &&
            (hasError ? (
              <Alert
                severity="error"
                icon={<ErrorOutlineOutlined />}
                sx={{
                  borderRadius: 3,
                  border: 1,
                  borderColor: 'error.light',
                  alignItems: 'center',
                  '& .MuiAlert-message': {
                    fontWeight: 700,
                  },
                }}
              >
                {t('health.error')}
              </Alert>
            ) : !ehrStatus.dataHasArrived ? (
              <Alert
                severity="info"
                icon={<InfoOutlined />}
                sx={{
                  borderRadius: 3,
                  border: 1,
                  borderColor: 'info.light',
                  alignItems: 'center',
                  '& .MuiAlert-message': {
                    fontWeight: 700,
                  },
                }}
              >
                {t('health.comingSoon')}
              </Alert>
            ) : (
              <>
                <Alert
                  severity="success"
                  icon={<CheckCircleOutlineOutlined />}
                  sx={{
                    mb: 3,
                    borderRadius: 3,
                    border: 1,
                    borderColor: 'success.light',
                    alignItems: 'flex-start',
                    '& .MuiAlert-icon': {
                      mt: 0.4,
                    },
                  }}
                >
                  <Typography
                    variant="h6"
                    component="h2"
                    sx={{
                      fontWeight: 900,
                      mb: 0.75,
                      mt: -0.25,
                    }}
                  >
                    {t('health.watchTitle')}
                  </Typography>

                  <Typography variant="body1" component="p">
                    {t('health.watchText')}
                  </Typography>
                </Alert>

                <Paper
                  elevation={0}
                  sx={{
                    border: 1,
                    borderColor: 'divider',
                    borderRadius: 3,
                    overflow: 'hidden',
                    bgcolor: 'background.paper',
                    '& [role="tablist"]': {
                      borderBottom: 1,
                      borderColor: 'divider',
                      bgcolor: 'grey.50',
                    },
                    '& [role="tab"]': {
                      textTransform: 'none',
                      fontWeight: 800,
                    },
                  }}
                >
                  <Tabs tracked>
                    {flagActive(FEATURES.EHR_VISITS) && (
                      <EhrTab
                        title={t('health.visits.title')}
                        data={visits}
                        type="visits"
                        icon={<FaRegHospital />}
                        showCaveat
                      >
                        <VisitsList visits={visits.response || []} />
                      </EhrTab>
                    )}

                    {flagActive(FEATURES.EHR_CONDITIONS) && conditions.response?.length && (
                      <EhrTab
                        title={t('health.conditions.title')}
                        data={conditions}
                        type="conditions"
                        icon={<BsJournalMedical />}
                        showCaveat
                      >
                        <Conditions data={conditions.response || []} type="conditions" />
                      </EhrTab>
                    )}
                  </Tabs>
                </Paper>
              </>
            ))}
        </Box>
      </Paper>

      <Loader isVisible={isBusy} label={t('health.loader')} />
    </PortalLayout>
  );
}

export default HealthScene;
